import {
  type HttpClient,
  type HttpClientRequestOptions,
  type HttpClientResponse,
} from '@automation-suite/shared-clients-http';

import { Logger } from '@automation-suite/shared-clients-logger';
import { fetchResponseToHttpClientResponse } from '../utils/fetchResponseToHttpClientResponse';
import { isApplicationJson } from '../utils/isApplicationJson';

type FetchHttpClientOptions = {
  logger?: Logger;
};

export class FetchHttpClient implements HttpClient {
  private readonly logger?: Logger;

  constructor({ logger }: FetchHttpClientOptions = {}) {
    this.logger = logger;
  }

  private async fetchHttp<TRequest, TResponse>({
    body,
    method,
    options,
    url,
  }: {
    body: TRequest;
    method: string;
    options?: HttpClientRequestOptions;
    url: string;
  }): Promise<HttpClientResponse<TResponse>> {
    const contentType = options?.headers?.['Content-Type'] || 'application/json';

    this.logger?.info(`${this.constructor.name} Request`, {
      url,
      method,
      headers: options?.headers,
      body: body ? (typeof body === 'object' || typeof body === 'string' ? body : '(non-printable body)') : undefined,
    });

    const response = await fetch(url, {
      ...options,
      ...(body
        ? isApplicationJson(contentType)
          ? { body: JSON.stringify(body) }
          : { body: body as BodyInit | null | undefined }
        : {}),
      headers: {
        ...(body ? { 'Content-Type': contentType } : {}),
        ...options?.headers,
      },
      method,
    });

    const transformedResponse = await fetchResponseToHttpClientResponse<TResponse>(response);

    this.logger?.info(`${this.constructor.name} Response`, {
      url,
      status: response.status,
      statusText: response.statusText,
      body: transformedResponse.body,
    });

    return transformedResponse;
  }

  async delete<Response>(url: string, options?: HttpClientRequestOptions): Promise<HttpClientResponse<Response>> {
    return await this.fetchHttp<undefined, Response>({
      body: undefined,
      method: 'DELETE',
      options,
      url,
    });
  }

  async get<Response>(url: string, options?: HttpClientRequestOptions): Promise<HttpClientResponse<Response>> {
    return await this.fetchHttp<undefined, Response>({
      body: undefined,
      method: 'GET',
      options,
      url,
    });
  }

  async patch<Request, Response>(
    url: string,
    body: Request,
    options?: HttpClientRequestOptions
  ): Promise<HttpClientResponse<Response>> {
    return await this.fetchHttp<Request, Response>({
      body,
      method: 'PATCH',
      options,
      url,
    });
  }

  async post<Request, Response>(
    url: string,
    body: Request,
    options?: HttpClientRequestOptions
  ): Promise<HttpClientResponse<Response>> {
    return await this.fetchHttp<Request, Response>({
      body,
      method: 'POST',
      options,
      url,
    });
  }

  async put<Request, Response>(
    url: string,
    body: Request,
    options?: HttpClientRequestOptions
  ): Promise<HttpClientResponse<Response>> {
    return await this.fetchHttp<Request, Response>({
      body,
      method: 'PUT',
      options,
      url,
    });
  }
}
